/**
 * ComponentInventory — /w/:slug/app/systems/:systemId/components
 *
 * V3-9-9 (ADR-0025 §9). The AI System → Components surface. Design
 * cleared through the mandated ux-adversary three-critic panel
 * (aesthetic + accessibility + flow), 3 rounds, 0 open Blocker/High.
 * See .claude/skills/ux-adversary/runs/v3-9-component-inventory/.
 *
 * Load-bearing design decisions carried from the cleared mockup:
 *   - Confidence is a WORD on its own ramp: likely (neutral) / confirmed
 *     (accent) / accepted (green) — never a bare "AI".
 *   - Vendor-technical strings (purl, hashes, tool version) live ONLY
 *     behind the role-gated Verify drawer, never in primary chrome.
 *   - Row severity is encoded by a left-rule whose weight tracks the
 *     component's MAX severity (danger > warn > confirmed).
 *   - viewer role sees no Accept/Reject and no Verify (write + raw doc).
 *   - Every color routes through a DS var(--…) token — no hardcoded hex.
 */

import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Badge, Button, Card, EmptyState, PageHeader, Spinner,
} from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  listSystemComponents, listSystemSnapshots, setComponentClassification, mintBomToken,
  type ComponentRow, type SnapshotRow,
} from '../lib/api';
import { fmtDate } from '../lib/format';

// V3-9-8 — copy-paste CI snippets, pinned by commit SHA. The token is a
// placeholder until the owner mints a system-scoped bom:write token.
function ciSnippets(systemId: string, token: string): { tool: string; code: string }[] {
  const auth = `-H "Authorization: Bearer ${token}"`;
  const url = `https://gacpro.io/api/ci/systems/${systemId}/boms`;
  const sha = '"$GIT_COMMIT_SHA"';
  return [
    { tool: 'Syft', code: `syft . -o cyclonedx-json > sbom.json\ncurl -sf ${auth} \\\n  -H "X-Commit-Sha: ${sha}" \\\n  --data-binary @sbom.json \\\n  "${url}?format=cyclonedx&context=source"` },
    { tool: 'cdxgen', code: `cdxgen -t js -o sbom.json --spec-version 1.6\ncurl -sf ${auth} \\\n  -H "X-Commit-Sha: ${sha}" \\\n  --data-binary @sbom.json \\\n  "${url}?format=cyclonedx&context=source"` },
    { tool: 'npm sbom', code: `npm sbom --sbom-format cyclonedx > sbom.json\ncurl -sf ${auth} \\\n  -H "X-Commit-Sha: ${sha}" \\\n  --data-binary @sbom.json \\\n  "${url}?format=cyclonedx&context=source"` },
  ];
}

const AI_ROLE_LABEL: Record<string, string> = {
  llm_sdk: 'LLM SDK', orchestration: 'Orchestration', vector_store: 'Vector store',
  model_file: 'Model file', mcp_server: 'MCP server', eval_tool: 'Eval tool',
  guardrail: 'Guardrail', model_ref: 'Model reference',
};

const LICENSE_LABEL: Record<string, string> = {
  permissive: 'permissive', weak_copyleft: 'weak copyleft', copyleft: 'copyleft',
  proprietary: 'proprietary', unknown: 'unknown',
};

/** Confidence chip on its own ramp (cleared design H1). */
function ConfidenceChip({ status, confidence }: { status: string; confidence: number | null }): React.ReactElement {
  if (status === 'accepted') {
    return (
      <span style={chip('var(--success)', 'var(--success)')}>
        <span aria-hidden>✓</span> accepted
      </span>
    );
  }
  const confirmed = (confidence ?? 0) >= 0.75;
  return confirmed ? (
    <span style={chip('var(--accent)', 'var(--accent)')}>
      <span aria-hidden>◆</span> confirmed AI
    </span>
  ) : (
    <span style={chip('var(--border-strong)', 'var(--text-muted)')}>
      <span aria-hidden>◐</span> likely AI
    </span>
  );
}

function chip(border: string, color: string): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '2px 8px', borderRadius: 'var(--radius-chip)',
    border: `1px solid ${border}`, color, fontSize: 'var(--font-size-2xs)', fontWeight: 600,
    whiteSpace: 'nowrap',
  };
}

/** A component's max severity → the left-rule token + width. */
function rowSeverity(c: ComponentRow): { color: string; width: string } {
  const regressed = c.license_class === 'copyleft' || c.license_class === 'weak_copyleft';
  if (c.ai_role && c.classification_status === 'recommended' && (c.ai_confidence ?? 0) >= 0.75) {
    return { color: 'var(--accent)', width: '2px' }; // confirmed
  }
  if (regressed) return { color: 'var(--warning)', width: '3px' }; // warn
  if (c.classification_status === 'accepted') return { color: 'var(--success)', width: '2px' };
  return { color: 'var(--border)', width: '2px' };
}

export function ComponentInventory(): React.ReactElement {
  const { systemId = '' } = useParams();
  const ws = useOptionalWorkspace();
  const slug = ws?.workspace.slug ?? null;
  const canManage = ws?.role === 'owner' || ws?.role === 'admin';

  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openVerify, setOpenVerify] = useState<string | null>(null);
  const [ciTab, setCiTab] = useState(0);
  const [mintedToken, setMintedToken] = useState<string | null>(null);
  const [minting, setMinting] = useState(false);

  const comps = useWorkspaceQuery(
    (s) => queryKeys.components(s, systemId),
    (s) => listSystemComponents(s, systemId),
  );
  const snaps = useWorkspaceQuery(
    (s) => queryKeys.systemSnapshots(s, systemId),
    (s) => listSystemSnapshots(s, systemId),
  );

  const latest = snaps.data?.snapshots?.[0];
  const coverage = latest?.coverage_score != null ? Math.round(latest.coverage_score * 100) : null;
  const gaps = useMemo<string[]>(() => {
    try { return latest ? JSON.parse(latest.element_gaps_json) : []; } catch { return []; }
  }, [latest]);

  const needsReview = (comps.data?.components ?? []).filter(
    (c) => c.ai_role && c.classification_status === 'recommended',
  ).length;

  async function mint() {
    if (!slug) return;
    setMinting(true); setError(null);
    try {
      const t = await mintBomToken(slug, systemId);
      setMintedToken(t.plaintext);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to mint token');
    } finally {
      setMinting(false);
    }
  }

  async function decide(c: ComponentRow, status: 'accepted' | 'rejected') {
    if (!slug) return;
    setBusy(c.component_id); setError(null);
    try {
      await setComponentClassification(slug, systemId, c.component_id, status);
      comps.refetch(); snaps.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update classification');
    } finally {
      setBusy(null);
    }
  }

  if (comps.loading || snaps.loading) return <Spinner />;

  const components = comps.data?.components ?? [];
  const snapshots = snaps.data?.snapshots ?? [];

  if (snapshots.length === 0) {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
        <PageHeader title="Components" subtitle="What this AI system is built from — ingested from a Software Bill of Materials." />
        <EmptyState
          title="No SBOM yet"
          description="Push your first SBOM from CI, or connect a repository. We'll materialize the components and flag the AI pieces once we receive a BOM."
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <PageHeader
        title="Components"
        subtitle={`${latest?.component_count ?? components.length} components · ${needsReview} need review`}
      />

      {error && <Alert variant="error" title="Couldn't update">{error}</Alert>}

      {/* Coverage + gaps — a nudge, never an error state (neutral, not amber) */}
      <Card title="SBOM coverage">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text)' }} role="img" aria-label={`SBOM coverage ${coverage ?? 0} percent`}>
            {coverage ?? '—'}%
          </div>
          {gaps.length > 0 && (
            <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>
              <span aria-hidden>◇</span> missing: {gaps.join(', ')}
            </div>
          )}
        </div>
      </Card>

      {/* V3-9-8 — Push an SBOM from CI (owner/admin) */}
      {canManage && (
        <Card title="Push an SBOM from CI">
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', flexWrap: 'wrap' }} role="tablist" aria-label="SBOM tool">
            {ciSnippets(systemId, mintedToken ?? 'YOUR_TOKEN').map((s, i) => (
              <button
                key={s.tool}
                role="tab"
                aria-selected={ciTab === i}
                onClick={() => setCiTab(i)}
                style={{
                  padding: '4px 12px', minHeight: '32px', borderRadius: 'var(--radius-chip)', cursor: 'pointer',
                  border: `1px solid ${ciTab === i ? 'var(--accent)' : 'var(--border-strong)'}`,
                  color: ciTab === i ? 'var(--accent)' : 'var(--text-muted)',
                  background: 'transparent', fontSize: 'var(--font-size-xs)', fontWeight: 600,
                }}
              >
                {s.tool}
              </button>
            ))}
          </div>
          <pre style={{ margin: 0, padding: 'var(--space-3)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input)', overflowX: 'auto', fontSize: 'var(--font-size-2xs)', color: 'var(--text)', whiteSpace: 'pre' }}>
            {ciSnippets(systemId, mintedToken ?? 'YOUR_TOKEN')[ciTab].code}
          </pre>
          <div style={{ marginTop: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            {!mintedToken ? (
              <Button variant="secondary" disabled={minting} onClick={mint}>Mint a push token (this system only)</Button>
            ) : (
              <div style={{ display: 'grid', gap: '4px' }}>
                <label htmlFor="bomtok" style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>Push token — copy it now, you won't see it again</label>
                <input id="bomtok" readOnly value={mintedToken} aria-label="Push token value"
                  style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-2xs)', padding: '6px 8px', minHeight: '36px', width: 'min(28rem, 100%)', background: 'var(--surface)', border: '1px solid var(--control-border, var(--border-strong))', borderRadius: 'var(--radius-input)', color: 'var(--text)' }} />
              </div>
            )}
            <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-2xs)' }}>
              We redact URL credentials, internal hostnames, and author emails before storing anything.
            </span>
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 'var(--space-6)', alignItems: 'start' }}>
        {/* Component list */}
        <Card title="Inventory">
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-2)' }}>
            {components.map((c) => {
              const sev = rowSeverity(c);
              const regressed = c.license_class === 'copyleft' || c.license_class === 'weak_copyleft';
              const canDecide = canManage && c.ai_role && c.classification_status === 'recommended';
              return (
                <li
                  key={c.component_id}
                  style={{
                    borderLeft: `${sev.width} solid ${sev.color}`,
                    background: 'var(--surface-elevated)',
                    border: '1px solid var(--border)',
                    borderLeftWidth: sev.width, borderLeftColor: sev.color,
                    borderRadius: 'var(--radius-input)', padding: 'var(--space-3)',
                    display: 'grid', gap: '6px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text)' }}>{c.name}</span>
                    {c.ai_role && <ConfidenceChip status={c.classification_status} confidence={c.ai_confidence} />}
                    {c.ai_role && <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>{AI_ROLE_LABEL[c.ai_role] ?? c.ai_role}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    <span>license: {LICENSE_LABEL[c.license_class] ?? c.license_class}{regressed ? ' (regressed)' : ''}</span>
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => setOpenVerify(openVerify === c.component_id ? null : c.component_id)}
                        style={verifyBtn}
                        aria-expanded={openVerify === c.component_id}
                      >
                        Verify · technical detail
                      </button>
                    )}
                  </div>

                  {/* Verify drawer — owner/admin only; the ONLY place vendor strings appear */}
                  {canManage && openVerify === c.component_id && (
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-input)', padding: 'var(--space-2)', fontSize: 'var(--font-size-2xs)', color: 'var(--text-dim)', overflowX: 'auto' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' }}>OWNER / ADMIN ONLY</div>
                      <div style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{c.purl_canonical}</div>
                      {c.version && <div>version {c.version} · depth {c.depth} · {c.scope}</div>}
                      {c.license_spdx && <div>SPDX {c.license_spdx}</div>}
                    </div>
                  )}

                  {/* Accept / reject — owner/admin, recommended AI rows only */}
                  {canDecide && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <Button variant="secondary" disabled={busy === c.component_id} onClick={() => decide(c, 'accepted')}>
                        Accept AI classification
                      </Button>
                      <Button variant="ghost" disabled={busy === c.component_id} onClick={() => decide(c, 'rejected')}>
                        Reject
                      </Button>
                      <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-2xs)' }}>
                        Accepted → feeds EU AI Act disclosure + rule checks
                      </span>
                    </div>
                  )}
                  {c.classification_status === 'accepted' && (
                    <div style={{ color: 'var(--success)', fontSize: 'var(--font-size-2xs)' }}>
                      <span aria-hidden>✓</span> attested{c.accepted_at ? ` · ${fmtDate(c.accepted_at)}` : ''}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>

        {/* Snapshot timeline */}
        <Card title="History">
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-3)' }}>
            {snapshots.map((s, i) => (
              <li key={s.snapshot_id} style={{ display: 'grid', gap: '2px', borderBottom: i < snapshots.length - 1 ? '1px solid var(--border)' : 'none', paddingBottom: 'var(--space-2)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <Badge tone={s.trust === 'attested' ? 'accent' : 'neutral'}>{s.source.replace('_', ' ')}</Badge>
                  <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>{fmtDate(s.published_at)}</span>
                </div>
                <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-2xs)' }}>
                  {s.component_count} components · coverage {s.coverage_score != null ? `${Math.round(s.coverage_score * 100)}%` : '—'}
                  {coverageDeltaLabel(snapshots, i)}
                </div>
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </div>
  );
}

function coverageDeltaLabel(snaps: SnapshotRow[], i: number): string {
  const cur = snaps[i]?.coverage_score;
  const prev = snaps[i + 1]?.coverage_score;
  if (cur == null || prev == null) return '';
  const d = Math.round((cur - prev) * 100);
  if (d === 0) return '';
  return d < 0 ? ` · dropped ${Math.abs(d)}%` : ` · +${d}%`;
}

const verifyBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', color: 'var(--accent)',
  cursor: 'pointer', padding: '4px 0', minHeight: '24px', fontSize: 'var(--font-size-xs)',
  textDecoration: 'underline',
};
